/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import {
  useRequestChallengesMostRecent,
  useRequestUsersMeSettings,
} from "@packages/sdk";
import { useCallback, useEffect, useRef } from "react";

import type { ThemeType } from "../../../../global/emotion";
import { spacing } from "../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import type { Color } from "../../../types/color.emotion.types";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { Mask } from "../../_base/_core/Mask/Mask.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import type { AuthFlowProps, AuthProps } from "./AuthTypes.emotion";

const cssButton: Interpolation = {
  [cssMediaQueryMinWidthTablet]: {
    width: "fit-content",
  },
};
const cssButtons: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  left: 0,
  paddingBottom: spacing.xl,
  paddingLeft: spacing.ml,
  paddingRight: spacing.ml,
  position: "fixed",
  width: "100%",
  zIndex: theme.zIndices.modalOverlay,
  [cssMediaQueryMinWidthTablet]: {
    alignItems: "center",
    flexDirection: "row-reverse",
    gap: spacing.m,
    left: "calc(calc(100vw - 720px) / 2)",
    justifyContent: "end",
    maxWidth: 720,
    width: "100%",
    zIndex: theme.zIndices[2],
  },
});
const cssChallenge: Interpolation<ThemeType> = (theme) => ({
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  borderRadius: theme.radius.m,
  flexShrink: 0,
  height: "min(200px, 40vh)",
  minWidth: "100%",
  overflowX: "hidden",
  position: "relative",
  scrollSnapAlign: "start",
  width: "100%",
  [cssMediaQueryMinWidthTablet]: {
    minWidth: 325,
    width: 325,
  },
});
const cssChallengeContent: Interpolation<ThemeType> = (theme) => ({
  color: "white",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xxs,
  maxWidth: "100%",
  overflowX: "hidden",
  padding: theme.spacing.l,
  position: "absolute",
  bottom: 0,
  left: 0,
  zIndex: theme.zIndices[2],
});
const cssChallengeCount: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.shades.shade20,
  borderRadius: theme.radius.xxs,
  color: "white",
  left: theme.spacing.l,
  paddingBottom: theme.spacing.xxs,
  paddingLeft: theme.spacing.s,
  paddingRight: theme.spacing.s,
  paddingTop: theme.spacing.xxs,
  position: "absolute",
  top: theme.spacing.m,
});
const cssChallengeDescription: Interpolation<ThemeType> = (theme) => ({
  color: "white",
  opacity: 0.9,
});
const cssChallenges: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  gap: theme.spacing.m,
  overflowX: "auto",
  scrollSnapType: "x mandatory",
  scrollBehavior: "smooth",
  scrollPaddingLeft: 0,
  margin: "0 auto",
  maxWidth: 720,
  width: spacing.full,
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});
const cssContainer: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.m,
  justifyContent: "flex-start",
  padding: spacing.ml,
  width: spacing.full,
  [cssMediaQueryMinWidthTablet]: {
    justifyContent: "center",
  },
};
const cssMask: Interpolation<ThemeType> = (theme) => ({
  borderBottomLeftRadius: theme.radius.m,
  borderBottomRightRadius: theme.radius.m,
  bottom: 0,
  left: 0,
  position: "absolute",
  zIndex: theme.zIndices[1],
});
const cssSubtitle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedHigh,
});
const cssTextContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.s,
  maxWidth: 720,
  margin: "0 auto",
  [cssMediaQueryMinWidthTablet]: {
    gap: theme.spacing.m,
  },
});
const cssTextContainerDesktop: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: {
    display: "flex",
    marginBottom: 24,
    marginTop: -64,
  },
};
const cssTextContainerMobile: Interpolation = {
  display: "flex",
  [cssMediaQueryMinWidthTablet]: {
    display: "none",
  },
};

const CHALLENGES = [
  {
    color_hex: "#491A22",
    description: "sms_prompt_challenge_1_description",
    image_url: "https://images.hallow.app/covers/1610_l.webp",
    title: "sms_prompt_challenge_1_title",
    user_count: 500000,
  },
  {
    color_hex: "#434F8A",
    description: "sms_prompt_challenge_2_description",
    image_url: "https://images.hallow.app/covers/1757_l.webp",
    title: "sms_prompt_challenge_2_title",
    user_count: 145000,
  },
  {
    color_hex: "#2850B7",
    description: "sms_prompt_challenge_3_description",
    image_url: "https://images.hallow.app/covers/2236_l.webp",
    title: "sms_prompt_challenge_3_title",
    user_count: 186000,
  },
  {
    color_hex: "#522B2D",
    description: "sms_prompt_challenge_4_description",
    image_url: "https://images.hallow.app/covers/2112_l.webp",
    title: "sms_prompt_challenge_4_title",
    user_count: 1800000,
  },

  /** This one should only display if the current challenge is null */
  {
    color_hex: "#000D1C",
    description: "sms_prompt_challenge_5_description",
    image_url: "https://images.hallow.app/covers/1426_l.webp",
    title: "sms_prompt_challenge_5_title",
    user_count: 78800,
  },
];
const FORMAT = Intl.NumberFormat(undefined, {
  compactDisplay: "short",
  maximumFractionDigits: 0,
  notation: "compact",
  useGrouping: true,
});

const Challenge = ({
  backgroundImage,
  colorHex,
  description,
  isTranslated,
  title,
  userCount,
}: {
  backgroundImage: string;
  colorHex: Color;
  description: string;
  isTranslated: boolean;
  title: string;
  userCount: number;
}) => {
  const t = useTranslations();

  return (
    <div
      css={cssChallenge}
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <Text css={cssChallengeCount} size="l" type="detail">
        {t("challenge_tile_praying", {
          "0": FORMAT.format(userCount),
        })}
      </Text>
      <div css={cssChallengeContent}>
        <Text overflow="ellipsis" size="l" type="title">
          {isTranslated ? title : t(title)}
        </Text>
        <Text
          css={cssChallengeDescription}
          overflow="ellipsis"
          size="s"
          type="body"
        >
          {isTranslated ? description : t(description)}
        </Text>
      </div>
      <Mask color={colorHex} css={cssMask} direction="to top" size="l" />
    </div>
  );
};

export const AuthSmsPromptFlow = ({
  setFlow,
}: Omit<AuthFlowProps, "onAuth"> & Pick<AuthProps, "onAuth">) => {
  const { track } = useAnalytics();
  const { query: queryChallengesMostRecent } = useRequestChallengesMostRecent();
  const { mutationPut: mutationPutSettings } = useRequestUsersMeSettings();
  const t = useTranslations();
  const challengesRef = useRef<HTMLDivElement>(null);
  useViewedScreen({ screen: "sms_outside_hours_prompt" });

  useEffect(() => {
    if (challengesRef.current) challengesRef.current.scrollLeft = 0;
  }, [queryChallengesMostRecent.data]);

  const onContinue = useCallback(() => {
    track({
      event: HallowAnalyticsEvent.CompletedOnboardingStep,
      properties: {
        screen_name: "sms_outside_hours_prompt",
      },
    });
    mutationPutSettings.mutate({
      settings: [
        {
          key: "has_seen_sms_outside_hours_consent_popup",
          scope: "global",
          value: "true",
        },
      ],
    });
    setFlow("names");
  }, [mutationPutSettings, setFlow, track]);

  const onClickRemindMe = () => {
    track({
      event: HallowAnalyticsEvent.TappedPrimaryCTA,
      properties: {
        screen_name: "sms_outside_hours_prompt",
        text: "Remind Me",
      },
    });
    mutationPutSettings.mutate({
      settings: [
        {
          key: "sms_outside_hours_permitted",
          scope: "global",
          value: "true",
        },
      ],
    });
    onContinue();
  };
  const onClickNotNow = () => {
    track({
      event: HallowAnalyticsEvent.TappedSecondaryCTA,
      properties: {
        screen_name: "sms_outside_hours_prompt",
        text: "Not Now",
      },
    });
    mutationPutSettings.mutate({
      settings: [
        {
          key: "sms_outside_hours_permitted",
          scope: "global",
          value: "false",
        },
      ],
    });
    onContinue();
  };

  return (
    <>
      <div css={cssContainer}>
        <div css={[cssTextContainer, cssTextContainerDesktop]}>
          <Text type="headline" size="m" as="h2">
            {t("sms_prompt_onboarding_title")}
          </Text>
          <Text css={cssSubtitle} type="body" size="l" as="p">
            {t("sms_prompt_onboarding_subtitle")}
          </Text>
        </div>
        <div ref={challengesRef} css={cssChallenges}>
          {queryChallengesMostRecent.data && (
            <Challenge
              backgroundImage={queryChallengesMostRecent.data.images.medium}
              colorHex={
                queryChallengesMostRecent.data.images.color_hex as Color
              }
              description={queryChallengesMostRecent.data.short_desc}
              isTranslated
              title={queryChallengesMostRecent.data.title}
              userCount={queryChallengesMostRecent.data.users_count}
            />
          )}
          {(queryChallengesMostRecent.data
            ? CHALLENGES.slice(0, CHALLENGES.length - 1)
            : CHALLENGES
          ).map((challenge) => (
            <Challenge
              backgroundImage={challenge.image_url}
              colorHex={challenge.color_hex as Color}
              description={challenge.description}
              key={challenge.title}
              isTranslated={false}
              title={challenge.title}
              userCount={challenge.user_count}
            />
          ))}
        </div>
        <div css={[cssTextContainer, cssTextContainerMobile]}>
          <Text type="headline" size="m" as="h2">
            {t("sms_prompt_onboarding_title")}
          </Text>
          <Text css={cssSubtitle} type="body" size="l" as="p">
            {t("sms_prompt_onboarding_subtitle")}
          </Text>
        </div>
      </div>
      <div css={cssButtons}>
        <Button css={cssButton} isFullWidth onClick={onClickRemindMe} size="l">
          <I18nMessage message="remind_me" />
        </Button>
        <Button
          css={cssButton}
          isFullWidth
          onClick={onClickNotNow}
          size="l"
          variant="transparentPrimary"
        >
          <I18nMessage message="general_phrase_not_now" />
        </Button>
      </div>
    </>
  );
};
